import type { QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { type ResourcePathType, Resources } from "Infrastructure/Translations/Resources";
import { array, object, string } from "yup";
import type { InvestmentPlan } from "./types";
import type { RuleState } from "./useRuleEngine";

export const getQuestionnaireStepSchema = (
  questions: QuestionnaireQuestionDto[],
  allQuestions: QuestionnaireQuestionDto[],
  t: (resourcePath: ResourcePathType, options?: any) => string,
  ruleState: RuleState,
  investmentPlans: InvestmentPlan[] = [],
) => {
  const schemaShape: Record<string, any> = {};

  const getValidationRule = (type: string, isRequired: boolean) => {
    let baseSchema: any = type === "MultiCheckbox" ? array() : string();

    if (isRequired) {
      if (type === "MultiCheckbox") {
        baseSchema = baseSchema.min(1, t(Resources.Validation.Array.Min, { count: 1 })).required(t(Resources.Validation.Required));
      } else {
        baseSchema = baseSchema.required(t(type === "Text" ? Resources.Validation.Required : Resources.Questionnaire.Validation.SelectOne));
      }
    } else {
      baseSchema = baseSchema.nullable().notRequired();
    }

    return baseSchema;
  };

  for (const q of questions) {
    if (q.questionnaireQuestionItems.length === 0 && q.type !== "Text") {
      continue;
    }
    const isRequired =
      q.isRequired && !ruleState.isQuestionAllItemsDisabled(q.questionnaireQuestionItems.map((item) => item.questionnaireQuestionItemID));

    const applyChildrenRules = (mainFieldName: string) => {
      if (!q.questionnaireQuestionItems) return;

      for (const option of q.questionnaireQuestionItems) {
        if (!option.childrenQuestionItems || option.childrenQuestionItems.length === 0) continue;

        const isPlanSpecific = mainFieldName.includes("_");
        const planExternalId = isPlanSpecific ? mainFieldName.split("_")[1] : null;
        const parentQuestionId = isPlanSpecific ? mainFieldName.split("_")[0] : null;

        const hasMultiCheckbox = option.childrenQuestionItems.some((c) => c.type === "MultiCheckbox");

        if (hasMultiCheckbox) {
          const childGroupFieldName = isPlanSpecific
            ? `${parentQuestionId}_childGroup_${planExternalId}`
            : `childGroup_${option.questionnaireQuestionItemID}`;

          schemaShape[childGroupFieldName] = array()
            .nullable()
            .when(mainFieldName, (val: any, schema: any) => {
              const currentValue = Array.isArray(val) ? val[0] : val;
              const isSelected = Array.isArray(currentValue)
                ? currentValue.includes(option.questionnaireQuestionItemID.toString())
                : currentValue === option.questionnaireQuestionItemID.toString();

              return isSelected
                ? schema.min(1, t(Resources.Validation.Array.Min, { count: 1 })).required(t(Resources.Validation.Required))
                : schema.notRequired();
            });
        }

        for (const childItem of option.childrenQuestionItems) {
          if (childItem.type === "MultiCheckbox") continue;

          const childFieldName = isPlanSpecific
            ? `${parentQuestionId}_${childItem.questionnaireQuestionItemID}_${planExternalId}`
            : childItem.questionnaireQuestionItemID.toString();

          schemaShape[`${childFieldName}${childItem.type === "Number" ? "___Value" : ""}`] = string().when(
            mainFieldName,
            (val: any, schema: any) => {
              const currentValue = Array.isArray(val) ? val[0] : val;
              const isSelected = Array.isArray(currentValue)
                ? currentValue.includes(option.questionnaireQuestionItemID.toString())
                : currentValue === option.questionnaireQuestionItemID.toString();

              return isSelected ? schema.required(t(Resources.Validation.Required)) : schema.notRequired();
            },
          );
        }
      }
    };

    if (q.parentQuestionID !== null) {
      const parentQuestion = allQuestions.find((qu) => qu.questionnaireQuestionID === q.parentQuestionID);
      if (parentQuestion?.subType === "InvestmentPlan") {
        for (const plan of investmentPlans) {
          if (!ruleState.isQuestionVisible(parentQuestion.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId)) {
            continue;
          }
          if (!ruleState.isQuestionVisible(q.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId)) {
            continue;
          }
          const fieldName = `${q.questionnaireQuestionID}_${plan.nsureInvestmentPlanExternalId}${q.type === "Text" ? "___Value" : ""}`;
          schemaShape[fieldName] = getValidationRule(q.type, isRequired);
          applyChildrenRules(fieldName);
        }
      } else {
        if (!ruleState.isQuestionVisible(q.questionnaireQuestionID, null)) {
          continue;
        }
        const fieldName = `${q.questionnaireQuestionID}${q.type === "Text" ? "___Value" : ""}`;
        schemaShape[fieldName] = getValidationRule(q.type, isRequired);
        applyChildrenRules(fieldName);
      }
      continue;
    }

    if (q.subType === "InvestmentOnly") {
      for (const plan of investmentPlans) {
        const fieldName = `${q.questionnaireQuestionID}_${plan.nsureInvestmentPlanExternalId}${q.type === "Text" ? "___Value" : ""}`;
        schemaShape[fieldName] = getValidationRule(q.type, isRequired);
      }
      continue;
    }

    if (!ruleState.isQuestionVisible(q.questionnaireQuestionID, null)) {
      continue;
    }
    const fieldName = `${q.questionnaireQuestionID}${q.type === "Text" ? "___Value" : ""}`;
    schemaShape[fieldName] = getValidationRule(q.type, isRequired);
  }

  return object().shape(schemaShape);
};
